import { last } from "common-utils/utils/array";
import { InfiniteResourceKey } from "kreate-server/types";
import useSWRInfinite from "swr/infinite";

import { StatusIndicator } from "../types";

type OffsetLimit = {
  offset: number;
  limit: number;
};

type PageData<R> = {
  result: R;
  cursor: OffsetLimit | undefined;
};

/** @deprecated Use this instead: `apps/kreate-web/modules/resources/hooks/useCommandResultInfinite.ts` */
export function useOffsetPagination<P extends OffsetLimit, R>({
  initialParams,
  getKey,
  fetcher,
  getLength,
}: {
  initialParams: P | null | undefined;
  getKey: (params: P) => InfiniteResourceKey<P>;
  fetcher: (params: P) => Promise<R>;
  getLength: (result: R) => number;
}) {
  const swr = useSWRInfinite<PageData<R>>(
    (
      pageIndex: number,
      previousPageData: PageData<R> | undefined
    ): InfiniteResourceKey<P> | undefined => {
      if (!initialParams) return undefined;
      if (!pageIndex) return getKey(initialParams);
      const { offset, limit } = previousPageData?.cursor || {};
      if (offset == null || limit == null) return undefined;
      return getKey({ ...initialParams, offset, limit });
    },
    async ({ params }: { params: P }): Promise<PageData<R>> => {
      const result = await fetcher(params);
      const cursor =
        params.offset != null &&
        params.limit != null &&
        getLength(result) === params.limit
          ? { offset: params.offset + params.limit, limit: params.limit }
          : undefined;
      return { result, cursor };
    }
  );

  const isEnded = !swr.isLoading && swr.size > 0 && !last(swr.data)?.cursor;

  const loadMore =
    !swr.isLoading && !isEnded ? () => swr.setSize((s) => s + 1) : undefined;

  const isEmpty =
    !swr.isLoading &&
    swr.size > 0 &&
    swr.data?.every((d) => !getLength(d.result));

  const isLoadingMore =
    swr.isLoading || (!!swr.data && swr.size > swr.data.length);

  const statusIndicator: StatusIndicator = isLoadingMore
    ? "loading"
    : isEmpty
    ? "empty"
    : isEnded
    ? "ended"
    : "has-more";

  return {
    swr,
    loadMore,
    statusIndicator,
    initialParams,
  };
}
